import { t, Trans } from '@lingui/macro';
import React, { FormEvent, FunctionComponent, useEffect, useState } from 'react';

import { ApiKeySource, RotateApiKeyParams, rotateApiKey } from '@api/v4/organizations/api_keys';
import { PrimaryButton } from '@components/library/button';
import { DialogSizes, StandardDialog } from '@components/library/dialog';

import classes from '../styles/rotate-api-key-modal.module.scss';

import { ApiKeyFormValues, KEY_NAME_PATTERN, validateKeyName } from './shared/apiKeyForm';
import { ApiKeyFormFields } from './shared/ApiKeyFormFields';
import { GeneratedKeyPanel } from './shared/GeneratedKeyPanel';

// Managed keys confirm first, then edit only the expiry (name/description are
// inherited); legacy keys skip the confirmation and edit all three fields.
type ModalStep = 'confirm' | 'form' | 'generated';

interface RotateApiKeyModalProps {
  apiKeyId: string;
  keyDescription?: string;
  keyName: string;
  onRotated?: () => void;
  open: boolean;
  organizationKey: string;
  setOpen: (open: boolean) => void;
  source: ApiKeySource;
}

export const RotateApiKeyModal: FunctionComponent<RotateApiKeyModalProps> = ({
  apiKeyId,
  keyDescription = '',
  keyName,
  onRotated,
  open,
  organizationKey,
  setOpen,
  source,
}) => {
  const isLegacy = source === 'legacy';

  // Managed keys inherit their existing name/description (rendered disabled);
  // legacy keys start blank so the user names the materialized key.
  const getInitialValues = (): ApiKeyFormValues => ({
    description: isLegacy ? '' : keyDescription,
    expiryDate: '',
    name: isLegacy ? '' : keyName,
  });

  const [step, setStep] = useState<ModalStep>(isLegacy ? 'form' : 'confirm');
  const [values, setValues] = useState<ApiKeyFormValues>(getInitialValues);
  const [nameError, setNameError] = useState<string>('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [generatedToken, setGeneratedToken] = useState('');

  const resetModalState = (): void => {
    setStep(isLegacy ? 'form' : 'confirm');
    setValues(getInitialValues());
    setNameError('');
    setSubmitAttempted(false);
    setIsRotating(false);
    setGeneratedToken('');
  };

  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSubmitDisabled = isRotating || (isLegacy && (!values.name.trim() || !!validateKeyName(values.name)));

  const showGeneratedKey = (rotatedName: string, token: string): void => {
    setGeneratedToken(token);
    setStep('generated');

    Notify.create({
      title: t`Key ‘${rotatedName}’ rotated successfully`,
      type: 'success',
    });
    onRotated?.();
  };

  const performRotation = async (params: RotateApiKeyParams): Promise<void> => {
    setIsRotating(true);
    try {
      const response = await rotateApiKey(organizationKey, apiKeyId, params);
      const { attributes } = response.data;
      showGeneratedKey(attributes.name || keyName, attributes.token || '');
    } catch (error) {
      Notify.create({
        body: (error instanceof Error && error.message) || t`Please try again or contact support`,
        title: t`Error rotating API Key`,
        type: 'error',
      });
    } finally {
      setIsRotating(false);
    }
  };

  const handleConfirmRotate = (): void => {
    setStep('form');
  };

  const handleNameChange = (name: string): void => {
    setValues((prev) => ({ ...prev, name }));

    if (submitAttempted) {
      setNameError(validateKeyName(name));
      return;
    }

    const trimmed = name.trim();
    if (!trimmed) {
      setNameError(t`Name is required`);
    } else if (!KEY_NAME_PATTERN.test(trimmed)) {
      setNameError(t`Name can only contain letters, numbers, hyphens, underscores, and spaces`);
    } else {
      setNameError('');
    }
  };

  const handleDescriptionChange = (description: string): void => {
    setValues((prev) => ({ ...prev, description }));
  };

  const handleExpiryChange = (expiresAt: string): void => {
    setValues((prev) => ({ ...prev, expiryDate: expiresAt }));
  };

  const handleFormSubmit = (e: FormEvent): void => {
    e.preventDefault();

    if (isLegacy) {
      setSubmitAttempted(true);
      const error = validateKeyName(values.name);
      setNameError(error);
      if (error) {
        return;
      }

      void performRotation({
        source: 'legacy',
        name: values.name.trim(),
        ...(values.description.trim() && { description: values.description.trim() }),
        ...(values.expiryDate && { expiresAt: values.expiryDate }),
      });
      return;
    }

    // Managed keys keep their name/description server-side; only expiry is sent.
    void performRotation({
      ...(values.expiryDate && { expiresAt: values.expiryDate }),
    });
  };

  const title = ((): string => {
    if (step === 'generated') return t`New API Key Generated`;
    if (step === 'confirm') return t`Rotate ${keyName}'s key`;
    return t`Rotate API Key`;
  })();

  return (
    <StandardDialog
      dialogBodyClassName={classes.rotateApiKeyModalBody}
      dialogClassName={classes.rotateApiKeyModal}
      dialogHeaderClassName={classes.rotateApiKeyModalHeader}
      id="rotate-api-key-modal"
      open={open}
      setOpen={setOpen}
      showClose={step !== 'generated'}
      showFooter={false}
      size={DialogSizes.Auto}
      title={title}
      width={520}
    >
      {step === 'generated' && (
        <GeneratedKeyPanel onDone={(): void => setOpen(false)} token={generatedToken} />
      )}

      {step === 'confirm' && (
        <div className={classes.content}>
          <p className={classes.message}>
            <Trans>
              Rotating this key will generate a new token. The old token will continue working for 15 days and then be
              automatically invalidated.
            </Trans>
          </p>

          <div className={classes.actions}>
            <PrimaryButton
              className={classes.rotateButton}
              onClick={handleConfirmRotate}
              size="medium"
              type="button"
            >
              <Trans>Rotate Key</Trans>
            </PrimaryButton>
          </div>
        </div>
      )}

      {step === 'form' && (
        <form className={classes.form} onSubmit={handleFormSubmit}>
          <p className={classes.message}>
            <Trans>Please note, this key will have full access to your account.</Trans>
          </p>

          <ApiKeyFormFields
            descriptionDisabled={!isLegacy}
            idPrefix="rotate-api-key"
            nameDisabled={!isLegacy}
            nameError={isLegacy ? nameError : ''}
            onDescriptionChange={handleDescriptionChange}
            onExpiryChange={handleExpiryChange}
            onNameChange={handleNameChange}
            values={values}
          />

          <div className={classes.actions}>
            <PrimaryButton
              className={classes.saveButton}
              disabled={isSubmitDisabled}
              isLoading={isRotating}
              loadingCopy={t`Rotating...`}
              size="medium"
              type="submit"
            >
              <Trans>Save &amp; Rotate</Trans>
            </PrimaryButton>
          </div>
        </form>
      )}
    </StandardDialog>
  );
};
